import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import imageData from "./images";
import { FaArrowLeft } from "react-icons/fa";
import Cookies from "js-cookie";
import { PiPersonArmsSpreadFill } from "react-icons/pi";
import { CiChat1 } from "react-icons/ci";
import { LiaEditSolid } from "react-icons/lia";
import { LuRotate3D } from "react-icons/lu";

// Separate components for better organization
const PersonaCard = ({ persona, index, onChatOpen }) => (
  <div className="mb-6">
    <p className="text-lg text-left bg-orng text-white w-1/2 py-2 px-4 rounded-t-xl font-bold uppercase">
      {persona.customer_segment}
    </p>
    <div className="rounded-xl bg-white px-8 py-4 flex flex-row gap-6 justify-center shadow-md hover:shadow-lg transition-shadow duration-200">
      <PersonaInfo persona={persona} index={index} onChatOpen={onChatOpen} />
      <PersonaDescription persona={persona} />
      <PersonaActions />
    </div>
  </div>
);

const PersonaInfo = ({ persona, index, onChatOpen }) => (
  <div className="w-1/4 flex flex-col gap-4 text-left text-sm justify-center">
    <div className="flex flex-row gap-4 items-center">
      <img src={imageData[index][0]} alt={persona.persona.name} className="w-24 h-24 rounded-full shadow-md" />
      <div className="flex flex-col font-semibold">
        <h3 className="font-bold mt-4">{persona.persona.name}</h3>
        <Demographics demographics={persona.persona.demographics} />
      </div>
    </div>
    <ChatButton persona={persona} index={index} onChatOpen={onChatOpen} />
  </div>
);

const Demographics = ({ demographics }) => (
  <>
    <p>Age: {demographics.age}</p>
    <p>Location: {demographics.city}</p>
    <p>{demographics.marital_status}</p>
    <p>{demographics.educational_background}</p>
    <p>{demographics.work}</p>
    <p>Monthly Income: {demographics.monthly_income}</p>
  </>
);

const ChatButton = ({ persona, index, onChatOpen }) => (
  <button
    onClick={() => onChatOpen(persona, index)}
    className="bg-white border-2 border-orng uppercase py-2 px-4 text-orng font-bold text-md rounded-xl flex flex-row gap-2 justify-center items-center hover:bg-orng hover:text-white transition-colors duration-200"
  >
    <CiChat1 size={24} />
    Chat with persona
  </button>
);

const PersonaDescription = ({ persona }) => (
  <div className="w-1/2 flex-grow flex flex-col gap-4 pt-2">
    <h3 className="text-xl font-semibold text-left uppercase">description</h3>
    <p className="text-left text-sm leading-relaxed">{persona.persona.description}</p>
  </div>
);

const PersonaActions = () => (
  <div className="w-1/5 flex-grow flex flex-col gap-8 justify-center">
    {[
      { icon: LuRotate3D, text: "Refine Hypothesis for research" },
      { icon: LiaEditSolid, text: "Generate interview questions" },
      { icon: PiPersonArmsSpreadFill, text: "connect with a real person" }
    ].map(({ icon: Icon, text }) => (
      <button key={text} className="bg-white text-black uppercase py-3 px-2 text-sm rounded-xl shadow-md flex flex-row gap-2 justify-center items-center hover:bg-gray-50 transition-colors duration-200">
        <Icon size={22} />
        {text}
      </button>
    ))}
  </div>
);

const ChatWindow = ({ isOpen, selectedPersona, selIndex, chatHistory, onClose, onSendMessage, loading }) => {
  const chatContainerRef = useRef(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage(message);
      setMessage("");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-10" onClick={onClose} />
      <PersonaDetails persona={selectedPersona} selIndex={selIndex} onClose={onClose} />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        className="fixed right-0 top-0 w-[45%] h-full bg-white p-8 flex flex-col z-30"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-semibold">Chat with {selectedPersona.persona.name}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <FaArrowLeft size={20} />
          </button>
        </div>

        <div ref={chatContainerRef} className="flex-grow overflow-y-auto mb-4 p-4">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orng" />
            </div>
          ) : (
            <ChatMessages messages={chatHistory} />
          )}
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            className="border rounded-lg px-4 py-2 flex-grow focus:outline-none focus:ring-2 focus:ring-orng"
            placeholder={`Ask ${selectedPersona.persona.name} anything`}
          />
          <button
            onClick={handleSend}
            className="px-4 py-2 bg-orng text-white rounded-lg hover:bg-opacity-90"
          >
            Send
          </button>
        </div>
      </motion.div>
    </>
  );
};

const ChatMessages = ({ messages }) => (
  <>
    {messages.map((chat, index) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, x: chat.sender === "user" ? 20 : -20, y: 10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className={`my-2 ${chat.sender === "user" ? "text-right" : "text-left"}`}
      >
        <p
          className={`inline-block p-3 rounded-lg max-w-[70%] ${
            chat.sender === "user"
              ? "bg-orng text-white rounded-br-none"
              : "bg-gray-100 rounded-bl-none"
          }`}
        >
          {chat.message}
        </p>
      </motion.div>
    ))}
  </>
);

const PersonaDetails = ({ persona, selIndex, onClose }) => (
  <motion.div
    initial={{ x: "100%" }}
    animate={{ x: 0 }}
    exit={{ x: "100%" }}
    className="fixed -left-64 top-0 px-6 w-2/3 h-full flex flex-col z-30 items-end gap-4 justify-center"
  >
    <button
      className="rounded-full bg-white p-4 shadow-md hover:bg-gray-50"
      onClick={onClose}
    >
      <FaArrowLeft size={24} />
    </button>

    <div className="bg-white shadow-lg rounded-xl">
    <div className="w-full bg-orng text-white p-4 rounded-t-xl">
    <h3 className="font-bold text-xl leading-tight">
      {persona.customer_segment.split(' ').slice(0, 5).join(' ')}
      {persona.customer_segment.split(' ').length > 5 ? "..." : ""}
    </h3>
    </div>

      {/* Persona Content */}
      <div className="flex items-center gap-4 mt-4 pl-4 pb-4 pr-8">
  <img
    src={imageData[selIndex][0]}
    alt={persona.persona.name}
    className="w-20 h-20 rounded-full"
  />
  <div className="text-left">
    <h3 className="font-bold text-xl mb-2">{persona.persona.name}</h3>
    <Demographics demographics={persona.persona.demographics} />
  </div>
</div>
    </div>

    {/* Second Card: Persona Description */}
    <div className="bg-white shadow-lg w-2/3 p-6 rounded-xl">
      <p className="text-sm leading-relaxed mt-4">{persona.persona.description}</p>
    </div>
  </motion.div>
);




const PersonaAnalyser = () => {
  const { state } = useLocation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [selIndex, setSelIndex] = useState(0);
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const personas = state?.data || [];
  const workspaceId = Cookies.get("workspaceId");
  const base_url = process.env.REACT_APP_BASE_URL;

  const handleChatOpen = async (persona, index) => {
    setSelectedPersona(persona);
    setSelIndex(index);
    setIsChatOpen(true);
    setLoading(true);

    try {
      const response = await axios.post(`${base_url}/get-persona-chat`, {
        workspace_id: workspaceId,
        persona_id: persona.persona.id,
      });
      setChatHistory(response.data.chats.sort((a, b) => 
        new Date(a.timestamp) - new Date(b.timestamp)
      ));
    } catch (error) {
      console.error("Error fetching chat history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (message) => {
    setChatHistory(prev => [...prev, { 
      sender: "user", 
      message, 
      timestamp: new Date().toISOString() 
    }]);

    try {
      const response = await axios.post(`${base_url}/send-persona-chat`, {
        message,
        workspace_id: workspaceId,
        persona_id: selectedPersona.persona.id,
      });
      setChatHistory(prev => [...prev, {
        sender: "persona",
        message: response.data.message,
        timestamp: new Date().toISOString(),
      }]);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleClose = () => {
    setIsChatOpen(false);
    setChatHistory([]);
  };

  return (
    <div className="min-h-screen py-4 px-6">
      <div className="flex flex-row gap-4 items-center mb-6">
        <button
          className="rounded-full bg-white px-2 py-2 shadow-md hover:bg-gray-50 transition-colors duration-200"
          onClick={() => window.location.href = "/overview"}
        >
          <FaArrowLeft size={24} />
        </button>
        <h2 className="uppercase text-4xl font-semibold">Customer Segmentation</h2>
      </div>

      <div className="space-y-6">
        {personas.map((persona, index) => (
          <PersonaCard
            key={index}
            persona={persona}
            index={index}
            onChatOpen={handleChatOpen}
          />
        ))}
      </div>

      <ChatWindow
        isOpen={isChatOpen}
        selectedPersona={selectedPersona}
        selIndex={selIndex}
        chatHistory={chatHistory}
        onClose={handleClose}
        onSendMessage={handleSendMessage}
        loading={loading}
      />
    </div>
  );
};

export default PersonaAnalyser;